import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Instagram, GraduationCap, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryPage = () => {
  const { t, language, isRTL } = useLanguage();
  const [gallery, setGallery] = useState([]);
  const [filteredGallery, setFilteredGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  // Helper to get full image URL
  const getImageUrl = (url) => {
    if (url.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setGallery(response.data);
        setFilteredGallery(response.data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // Filter gallery based on category
  const handleFilter = (filter) => {
    setActiveFilter(filter);
    if (filter === 'all') {
      setFilteredGallery(gallery);
    } else {
      setFilteredGallery(gallery.filter(img => img.category === filter));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8]">
        <div className="w-12 h-12 border-4 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#FDFCF8] min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className={`${isRTL ? 'text-right' : 'text-left'} max-w-3xl`}>
            <p className="font-manrope text-xs md:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              {t.gallery.subtitle}
            </p>
            <h1
              data-testid="gallery-title"
              className={`${isRTL ? 'font-amiri' : 'font-playfair'} text-4xl md:text-6xl font-medium text-[#1A1A1A] mb-6`}
            >
              {t.gallery.title}
            </h1>
            <div className="flex items-center gap-4">
              <a
                href="https://instagram.com/ninaceban_riyadh"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gallery-instagram-link"
                className="inline-flex items-center gap-2 font-manrope text-[#C5A059] hover:text-[#B08A3E] transition-colors duration-300"
              >
                <Instagram className="w-5 h-5" />
                @ninaceban_riyadh
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="pb-8">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="flex flex-wrap gap-3 justify-center md:justify-start">
            <Button
              onClick={() => handleFilter('all')}
              data-testid="filter-all"
              className={`rounded-full px-6 py-5 text-base font-medium transition-all duration-300 ${
                activeFilter === 'all'
                  ? 'bg-[#C5A059] text-white shadow-[0_4px_14px_0_rgba(197,160,89,0.39)]'
                  : 'bg-white text-[#1A1A1A] border border-[#E6E2D6] hover:border-[#C5A059] hover:text-[#C5A059]'
              }`}
            >
              {language === 'en' ? 'All Photos' : 'جميع الصور'}
            </Button>
            <Button
              onClick={() => handleFilter('courses')}
              data-testid="filter-courses"
              className={`rounded-full px-6 py-5 text-base font-medium transition-all duration-300 ${
                activeFilter === 'courses'
                  ? 'bg-[#C5A059] text-white shadow-[0_4px_14px_0_rgba(197,160,89,0.39)]'
                  : 'bg-white text-[#1A1A1A] border border-[#E6E2D6] hover:border-[#C5A059] hover:text-[#C5A059]'
              }`}
            >
              <GraduationCap className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {language === 'en' ? 'Courses & Training' : 'الدورات والتدريب'}
            </Button>
            <Button
              onClick={() => handleFilter('work')}
              data-testid="filter-work"
              className={`rounded-full px-6 py-5 text-base font-medium transition-all duration-300 ${
                activeFilter === 'work'
                  ? 'bg-[#C5A059] text-white shadow-[0_4px_14px_0_rgba(197,160,89,0.39)]'
                  : 'bg-white text-[#1A1A1A] border border-[#E6E2D6] hover:border-[#C5A059] hover:text-[#C5A059]'
              }`}
            >
              <Sparkles className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {language === 'en' ? 'My Work' : 'أعمالي'}
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="pb-20 md:pb-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          {filteredGallery.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#666660] font-manrope">
                {language === 'en' ? 'No photos in this category yet.' : 'لا توجد صور في هذه الفئة بعد.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredGallery.map((image, index) => (
                <div
                  key={image.id}
                  data-testid={`gallery-item-${index}`}
                  className="group relative aspect-square overflow-hidden rounded-xl cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={getImageUrl(image.url)}
                    alt={language === 'en' ? image.caption_en : image.caption_ar}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-4 w-full bg-gradient-to-t from-black/70 to-transparent">
                      <p className="font-manrope text-white text-sm text-center">
                        {language === 'en' ? image.caption_en : image.caption_ar}
                      </p>
                      <p className="font-manrope text-white/70 text-xs text-center mt-1 capitalize">
                        {image.category === 'courses' 
                          ? (language === 'en' ? 'Training' : 'تدريب')
                          : (language === 'en' ? 'Work' : 'عمل')
                        }
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="gallery-lightbox"
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={getImageUrl(selectedImage.url)}
              alt={language === 'en' ? selectedImage.caption_en : selectedImage.caption_ar}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-300"
            >
              <span className="text-white text-2xl">&times;</span>
            </button>
            {selectedImage.caption_en && (
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent rounded-b-lg">
                <p className="text-white text-lg text-center font-playfair">
                  {language === 'en' ? selectedImage.caption_en : selectedImage.caption_ar}
                </p>
                <p className="text-white/60 text-sm text-center mt-2 font-manrope capitalize">
                  {selectedImage.category === 'courses' 
                    ? (language === 'en' ? 'Courses & Training' : 'الدورات والتدريب')
                    : (language === 'en' ? 'My Work' : 'أعمالي')
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
